<template>
  <div class="personal-data-screen">
    <div class="personal-data-header">
      <h3>{{ t('accountInfo.personalData.title') }}</h3>
      <p class="personal-data-description">{{ t('accountInfo.personalData.description') }}</p>
    </div>

    <div class="form-section">
      <div class="form-group">
        <label class="form-label">{{ t('accountInfo.personalData.selectRequest') }}</label>
        <div class="radio-group">
          <div class="radio-option" :class="{ 'selected': selectedOption === 'export' }">
            <input 
              type="radio" 
              id="export-data"
              v-model="selectedOption" 
              value="export"
              class="radio-input"
              name="data-request"
            />
            <label for="export-data" class="radio-label">
              {{ t('accountInfo.personalData.exportData') }}
            </label>
          </div>
          <div class="radio-option" :class="{ 'selected': selectedOption === 'delete' }">
            <input 
              type="radio" 
              id="delete-data"
              v-model="selectedOption" 
              value="delete"
              class="radio-input"
              name="data-request"
            />
            <label for="delete-data" class="radio-label">
              {{ t('accountInfo.personalData.deleteData') }}
            </label>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="email" class="form-label required">
          {{ t('accountInfo.personalData.emailAddress') }}
        </label>
        <input
          type="email"
          id="email"
          v-model="emailAddress"
          :placeholder="t('accountInfo.personalData.emailPlaceholder')"
          class="form-input"
          :class="{ 'error': emailError }"
          required
        />
        <span v-if="emailError" class="error-message">{{ emailError }}</span>
      </div>

      <div class="form-actions">
        <button 
          @click="submitRequest" 
          :disabled="!canSubmit || isSubmitting"
          class="submit-button"
        >
          <span v-if="isSubmitting">{{ t('labels.Submitting') }}...</span>
          <span v-else>{{ t('accountInfo.personalData.sendRequest') }}</span>
        </button>
      </div>

      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <!-- Embedded Report View -->
      <div v-if="showReportView" class="report-view">
        <div class="report-header">
          <h4>{{ t('accountInfo.personalData.reportTitle') }}</h4>
          <div class="report-actions">
            <button @click="downloadPDF" class="report-button download-button">
              {{ t('accountInfo.personalData.downloadPDF') }}
            </button>
            <button @click="backToOptions" class="report-button back-button">
              {{ t('accountInfo.personalData.newRequest') }}
            </button>
          </div>
        </div>
        
        <PDFExport
          ref="pdfExportRef"
          :key="reportContentKey"
          :paper-size="'A4'"
          :margin="{ top: '1cm', left: '1cm', right: '1cm', bottom: '1cm' }"
          :file-name="`Personal_Data_Export_${requestDate}.pdf`"
          :scale="0.8"
          :force-page-break="'.pdf-page-break'"
        >
          <div class="report-content" v-html="reportHTML"></div>
        </PDFExport>
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { PwsProfileGeneralGet, DataPrivacyRequestCreate, PwsProfileAddressGet, TaxPayerGetList } from '../../api/AccountInfo'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'
import { PDFExport } from '@progress/kendo-vue-pdf'
import type { IPwsProfileGeneralGetResponse, IPwsProfileAddressGetResponse, IAddressWrapper, ITaxPayerGetListResponse } from '../../types/AccountInfoTypes'

const { t, locale } = useI18n()

const selectedOption = ref<string>('')
const emailAddress = ref<string>('')
const emailError = ref<string>('')
const isSubmitting = ref<boolean>(false)
const successMessage = ref<string>('')
const errorMessage = ref<string>('')
const profileData = ref<IPwsProfileGeneralGetResponse | null>(null)
const addressData = ref<IPwsProfileAddressGetResponse | null>(null)
const governmentIdsData = ref<ITaxPayerGetListResponse | null>(null)
const requestDate = ref<string>('')
const showReportView = ref<boolean>(false)
const pdfExportRef = ref<InstanceType<typeof PDFExport> | null>(null)
const reportContentKey = ref<number>(0) // Force re-render trigger

// Reset email error when user changes email address
watch(emailAddress, () => {
  if (emailError.value) {
    emailError.value = ''
  }
})

const canSubmit = computed(() => {
  return selectedOption.value && emailAddress.value && !emailError.value
})

const reportHTML = computed(() => {
  return generateReportHTML()
})

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}


const submitRequest = async () => {
  // Reset messages
  emailError.value = ''
  errorMessage.value = ''
  successMessage.value = ''

  // Validate email
  if (!emailAddress.value) {
    emailError.value = t('accountInfo.personalData.emailRequired')
    return
  }

  if (!validateEmail(emailAddress.value)) {
    emailError.value = t('accountInfo.personalData.emailInvalid')
    return
  }

  // Get authenticated user's bee number
  const beeNumber = await GetBeeNumberAuthenicatedUser()
  if (!beeNumber) {
    errorMessage.value = t('accountInfo.personalData.submitError')
    return
  }

  // Verify the entered email matches the one on file and fetch all data
  try {
    const [profile, addresses, governmentIds] = await Promise.all([
      PwsProfileGeneralGet({ beeNumber }),
      PwsProfileAddressGet({ beeNumber }),
      TaxPayerGetList({ beeNumber })
    ])
    
    profileData.value = profile // Store for PDF generation
    addressData.value = addresses // Store address data for PDF generation
    governmentIdsData.value = governmentIds // Store government IDs data for PDF generation
    
    const onFile = (profile?.item?.emailAddress || '').trim().toLowerCase()
    const entered = emailAddress.value.trim().toLowerCase()
    if (!onFile || onFile !== entered) {
      emailError.value = t('accountInfo.personalData.emailMismatch')
      return
    }
  } catch {
    // If profile lookup fails, surface a generic submit error
    errorMessage.value = t('accountInfo.personalData.submitError')
    return
  }

  if (!selectedOption.value) {
    errorMessage.value = t('accountInfo.personalData.optionRequired')
    return
  }

  try {
    isSubmitting.value = true

    // Get current date in ISO format
    const today = new Date().toISOString().split('T')[0]
    if (selectedOption.value === 'export') {
      requestDate.value = new Date().toISOString()
      showReportView.value = true
      successMessage.value = t('accountInfo.personalData.exportSuccess')
      // Force re-render of PDF content to ensure addresses are included
      reportContentKey.value += 1
    } else {
      // For delete requests, call the DataPrivacyRequestCreate API
      const response = await DataPrivacyRequestCreate({
        beeNumber,
        privacyRequestDate: today
      })

      // Check if request was successful (has sequence number)
      if (response?.privacySequenceNumber) {
        successMessage.value = t('accountInfo.personalData.deleteSuccess')
        // Reset form for delete requests
        selectedOption.value = ''
        emailAddress.value = ''
      } else {
        // No sequence number means it failed
        errorMessage.value = response?.responseStatus?.message || t('accountInfo.personalData.submitError')
      }
    }

  } catch {
    errorMessage.value = t('accountInfo.personalData.submitError')
  } finally {
    isSubmitting.value = false
  }
}


const downloadPDF = async () => {
  console.log('Download PDF button clicked!')
  
  if (!profileData.value) {
    console.error('No profile data available for PDF generation')
    errorMessage.value = t('accountInfo.personalData.pdfNoProfileDataError')
    return
  }
  
  if (!addressData.value) {
    console.error('No address data available for PDF generation')
    errorMessage.value = t('accountInfo.personalData.pdfNoAddressDataError')
    return
  }
  
  if (!pdfExportRef.value) {
    console.error('PDF export component not available')
    errorMessage.value = t('accountInfo.personalData.pdfComponentNotReadyError')
    return
  }
  
  try {
    console.log('Starting PDF export using PDFExport component...')
    console.log('Profile data available:', !!profileData.value)
    console.log('Address data available:', !!addressData.value)
    
    // Force re-render to ensure all content is updated
    reportContentKey.value += 1
    
    // Wait for DOM to be fully updated with address content
    await nextTick()
    
    await pdfExportRef.value.save()
    console.log('PDF export completed successfully')
  } catch (error) {
    console.error('Failed to generate PDF:', error)
    errorMessage.value = t('accountInfo.personalData.pdfDownloadFailedError')
  }
}

const backToOptions = () => {
  showReportView.value = false
  // Clear success message and reset form for new request
  successMessage.value = ''
  selectedOption.value = ''
  emailAddress.value = ''
}

const generateReportHTML = () => {
  console.log('generateReportHTML called, profileData:', !!profileData.value, 'addressData:', !!addressData.value)
  
  if (!profileData.value) return ''
  
  const profile = profileData.value.item
  const requestDateFormatted = new Date(requestDate.value).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  return `
    <div class="personal-data-report">
      <h1>${t('accountInfo.personalData.reportHeader')}</h1>
      
      <div class="report-section">
        <h2>${t('accountInfo.personalData.businessInformation')}</h2>
        <table class="data-table">
          <tr>
            <td><strong>${t('accountInfo.personalData.customerNumber')}</strong></td>
            <td>${profile?.beeNumber || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.dateOfBirth')}</strong></td>
            <td>${profile?.birthDate ? new Date(profile.birthDate).toLocaleDateString() : t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.username')}</strong></td>
            <td>${t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.anniversary')}</strong></td>
            <td>${t('accountInfo.personalData.notAvailable')}</td>
          </tr>
        </table>
      </div>

      <div class="report-section">
        <h2>${t('accountInfo.personalData.personalInformation')}</h2>
        <table class="data-table">
          <tr>
            <td><strong>${t('accountInfo.personalData.name')}</strong></td>
            <td>${profile?.firstName || ''} ${profile?.lastName || ''}</td>
            <td><strong>${t('accountInfo.personalData.webUrl')}</strong></td>
            <td>${profile?.webUrl || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.firstName')}</strong></td>
            <td class="highlight">${profile?.firstName || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.imHandle')}</strong></td>
            <td>${profile?.imHandle || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.lastName')}</strong></td>
            <td class="highlight">${profile?.lastName || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.facebook')}</strong></td>
            <td>${profile?.facebookId || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.preferredName')}</strong></td>
            <td>${profile?.preferredName || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.twitter')}</strong></td>
            <td>${profile?.twitterId || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.alternativeFirstName')}</strong></td>
            <td>${profile?.alternateFirstName || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.linkedIn')}</strong></td>
            <td>${profile?.linkedInId || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.alternativeLastName')}</strong></td>
            <td>${profile?.alternateLastName || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.pinterest')}</strong></td>
            <td>${profile?.pinterestId || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
        </table>
      </div>

      ${generateGovernmentIdsSection()}

      <div class="report-section pdf-page-break">
        <h2>${t('accountInfo.personalData.contactInformation')}</h2>
        <table class="data-table">
          <tr>
            <td><strong>${t('accountInfo.personalData.reportEmailAddress')}</strong></td>
            <td class="email-link">${profile?.emailAddress || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
        </table>
      </div>

      <div class="report-section">
        <h2>${t('accountInfo.personalData.phoneNumbers')}</h2>
        <table class="data-table bordered">
          <thead>
            <tr>
              <th>${t('accountInfo.personalData.home')}</th>
              <th>${t('accountInfo.personalData.mobile')}</th>
              <th>${t('accountInfo.personalData.fax')}</th>
              <th>${t('accountInfo.personalData.work')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${profile?.primaryPhone?.formattedNumber || profile?.primaryPhone?.number || t('accountInfo.personalData.notAvailable')}</td>
              <td>${profile?.altPhone1?.formattedNumber || profile?.altPhone1?.number || t('accountInfo.personalData.notAvailable')}</td>
              <td>${t('accountInfo.personalData.notAvailable')}</td>
              <td>${t('accountInfo.personalData.notAvailable')}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="report-section">
      ${generateAddressSection()}
      </div>

      <div class="report-footer pdf-page-break">
        <p><strong>${t('accountInfo.personalData.reportFooterText')} ${requestDateFormatted}</strong></p>
        <p>${t('accountInfo.personalData.requestType')}</p>
      </div>
    </div>
  `
}

const generateAddressSection = () => {
  console.log('generateAddressSection called, addressData:', !!addressData.value)
  
  if (!addressData.value?.item) {
    console.log('No address data found')
    return `
      <div class="report-section">
        <h2>${t('accountInfo.personalData.addresses')}</h2>
        <p>${t('accountInfo.personalData.noAddressesOnFile')}</p>
      </div>
    `
  }
  
  const addresses = addressData.value.item
  console.log('Address data found:', {
    shipping: !!addresses.shippingAddress,
    billing: !!addresses.billingAddress,
    mailing: !!addresses.mailingAddress,
    allAddresses: addresses.allAddresses?.length || 0
  })
  let addressHTML = ''
  
  // Helper function to format an address in table format
  const formatAddress = (address: IAddressWrapper, title: string) => {
    if (!address?.address) return ''
    
    const addr = address.address
    return `
      <div class="address-item" style="page-break-inside: avoid !important; break-inside: avoid !important; margin-bottom: 15px;">
        <h3 style="page-break-after: avoid !important; break-after: avoid !important;">${title}</h3>
        <table class="data-table" style="page-break-inside: avoid !important; break-inside: avoid !important;">
          <tr>
            <td><strong>${t('accountInfo.personalData.addressName')}</strong></td>
            <td>${addr.firstName || ''} ${addr.lastName || ''}</td>
            <td><strong>${t('accountInfo.personalData.company')}</strong></td>
            <td>${addr.companyName || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.addressLine1')}</strong></td>
            <td>${addr.addressLine1 || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.addressLine2')}</strong></td>
            <td>${addr.addressLine2 || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.city')}</strong></td>
            <td>${addr.city || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.stateProvince')}</strong></td>
            <td>${addr.stateCode || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
          <tr>
            <td><strong>${t('accountInfo.personalData.postalCode')}</strong></td>
            <td>${addr.postalCode || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.country')}</strong></td>
            <td>${addr.countryCode || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
        </table>
      </div>
    `
  }
  
  // Add shipping address
  if (addresses.shippingAddress) {
    addressHTML += formatAddress(addresses.shippingAddress, t('accountInfo.personalData.shippingAddress'))
  }
  
  // Add billing address (always show if exists)
  if (addresses.billingAddress) {
    addressHTML += formatAddress(addresses.billingAddress, t('accountInfo.personalData.billingAddress'))
  }
  
  // Add mailing address (always show if exists)
  if (addresses.mailingAddress) {
    addressHTML += formatAddress(addresses.mailingAddress, t('accountInfo.personalData.mailingAddress'))
  }
  
  return `
    <div class="report-section" style="page-break-inside: avoid !important; break-inside: avoid !important;">
      <h2 style="page-break-after: avoid !important; break-after: avoid !important;">${t('accountInfo.personalData.addresses')}</h2>
      <div style="page-break-inside: avoid !important; break-inside: avoid !important;">
        ${addressHTML || `<p>${t('accountInfo.personalData.noAddressesOnFile')}</p>`}
      </div>
    </div>
  `
}

const generateGovernmentIdsSection = () => {
  console.log('generateGovernmentIdsSection called, governmentIdsData:', !!governmentIdsData.value)
  
  if (!governmentIdsData.value?.items || governmentIdsData.value.items.length === 0) {
    console.log('No government IDs data found')
    return `
      <div class="report-section" style="page-break-inside: avoid !important; break-inside: avoid !important;">
        <h2 style="page-break-after: avoid !important; break-after: avoid !important;">${t('accountInfo.personalData.governmentIds')}</h2>
        <p>${t('accountInfo.personalData.noGovernmentIdsOnFile')}</p>
      </div>
    `
  }
  
  const allGovernmentIds = governmentIdsData.value.items
  console.log('All Government IDs data found:', allGovernmentIds.length, 'items')
  
  // Filter to get unique types with highest sequence number
  const uniqueGovernmentIds = new Map<string, typeof allGovernmentIds[0]>()
  
  allGovernmentIds.forEach(govId => {
    const existing = uniqueGovernmentIds.get(govId.governmentIdType)
    if (!existing || govId.beeTaxPayerSequence > existing.beeTaxPayerSequence) {
      uniqueGovernmentIds.set(govId.governmentIdType, govId)
    }
  })
  
  const filteredGovernmentIds = Array.from(uniqueGovernmentIds.values())
  console.log('Filtered to unique types with highest sequence:', filteredGovernmentIds.length, 'items')
  
  let governmentIdsHTML = ''
  
  filteredGovernmentIds.forEach((govId) => {
    governmentIdsHTML += `
      <div class="address-item" style="page-break-inside: avoid !important; break-inside: avoid !important; margin-bottom: 15px;">
        <h3 style="page-break-after: avoid !important; break-after: avoid !important;">${govId.governmentIdType}</h3>
        <table class="data-table" style="page-break-inside: avoid !important; break-inside: avoid !important;">
          <tr>
            <td><strong>${t('accountInfo.personalData.governmentIdType')}</strong></td>
            <td>${govId.governmentIdType || t('accountInfo.personalData.notAvailable')}</td>
            <td><strong>${t('accountInfo.personalData.taxPayerIdLastFour')}</strong></td>
            <td>${govId.taxPayerIdLastFour || t('accountInfo.personalData.notAvailable')}</td>
          </tr>
        </table>
      </div>
    `
  })
  
  return `
    <div class="report-section" style="page-break-inside: avoid !important; break-inside: avoid !important;">
      <h2 style="page-break-after: avoid !important; break-after: avoid !important;">${t('accountInfo.personalData.governmentIds')}</h2>
      <div style="page-break-inside: avoid !important; break-inside: avoid !important;">
        ${governmentIdsHTML}
      </div>
    </div>
  `
}


</script>

<style lang="scss" scoped>
.personal-data-screen {
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
}

.personal-data-header {
  margin-bottom: 30px;
}

.personal-data-header h3 {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 12px 0;
}

.personal-data-description {
  @include typography-body;
  color: #666;
  margin: 0;
}

.personal-data-form {
  background: white;
  border-radius: $radius-lg;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  overflow: hidden;
}

.form-section {
  padding: $spacing-8;
}

.form-group {
  margin-bottom: 24px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  @include typography-small;
  @include font-weight-semibold;
  display: block;
  color: #333;
  margin-bottom: 8px;
  width: 100%;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: $spacing-3;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  padding: $spacing-2;
  border-radius: $radius-md;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.radio-option:hover {
  background-color: rgba(var(--vt-c-teal-dark), 0.05);
}

.radio-option.selected {
  background-color: rgba(var(--vt-c-teal-dark), 0.1);
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.radio-option.selected .radio-label {
  @include font-weight-semibold;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.radio-input {
  width: 16px;
  height: 16px;
  accent-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  cursor: pointer;
  margin: 0;
  flex-shrink: 0;
}

.radio-option.selected .radio-input {
  accent-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  outline: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  outline-offset: 1px;
}

.radio-label {
  @include typography-small;
  color: #333;
  cursor: pointer;
  margin: 0;
  user-select: none;
  flex: 1;
}

.form-input {
  width: 100%;
  padding: $spacing-3 $spacing-4;
  @include typography-small;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1);
}

.form-input.error {
  border-color: #dc3545;
}

.form-actions {
  margin-top: 32px;
}

.submit-button {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  @include typography-small;
  @include font-weight-semibold;
  border: none;
  padding: $spacing-3 $spacing-6;
  border-radius: $button-radius-lg;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.submit-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.success-message {
  margin-top: 16px;
  padding: $spacing-3 $spacing-4;
  background-color: #d4edda;
  color: #155724;
  @include typography-small;
  border: 1px solid #c3e6cb;
  border-radius: $radius-md;
}

.error-message {
  @include typography-caption;
  margin-top: 8px;
  color: #dc3545;
}

.form-group .error-message {
  margin-top: 4px;
}

.export-options {
  margin-top: 24px;
  padding: $spacing-6;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.export-options h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin: 0 0 12px 0;
}

.export-options p {
  @include typography-small;
  color: #666;
  margin: 0 0 20px 0;
}

.export-buttons {
  display: flex;
  gap: $spacing-3;
  flex-wrap: wrap;
}

.export-button {
  padding: $spacing-3 $spacing-6;
  @include typography-small;
  @include font-weight-medium;
  border: none;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
}

.pdf-button {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
}

.pdf-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.reset-button {
  background-color: #6c757d;
  color: white;
}

.reset-button:hover {
  background-color: #5a6268;
}

.report-view {
  margin-top: 24px;
  background-color: var(--vt-c-white);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-5 $spacing-6;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e9ecef;
}

.report-header h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin: 0;
}

.report-actions {
  display: flex;
  gap: $spacing-3;
}

.report-button {
  padding: $spacing-2 $spacing-4;
  @include typography-small;
  @include font-weight-medium;
  border: none;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.2s ease;
}

.download-button {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
}

.download-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.back-button {
  background-color: #6c757d;
  color: white;
}

.back-button:hover {
  background-color: #5a6268;
}

.report-content {
  padding: $spacing-6;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: auto;
  width: 100%;
}

/* Report Content Styling */
.report-content :deep(.personal-data-report) {
  @include typography-caption;
  font-family: Arial, sans-serif;
  color: #000;
}

.report-content :deep(.personal-data-report h1) {
  @include typography-body-large;
  @include font-weight-bold;
  text-align: center;
  margin: 0 0 30px 0;
  color: #000;
}

.report-content :deep(.personal-data-report h2) {
  @include typography-small;
  @include font-weight-bold;
  margin: $spacing-5 0 $spacing-2-5 0;
  padding-bottom: $spacing-1;
  border-bottom: 1px solid #000;
  color: #000;
}

.report-content :deep(.report-section) {
  margin-bottom: 25px;
}

.report-content :deep(.data-table) {
  width: 100%;
  min-width: 500px;
  border-collapse: collapse;
  margin-bottom: 15px;
  table-layout: auto;
}

.report-content :deep(.data-table td) {
  padding: $spacing-1 $spacing-2;
  vertical-align: top;
  border: none;
}

.report-content :deep(.data-table td:first-child) {
  @include font-weight-regular;
  min-width: 120px;
  white-space: nowrap;
}

.report-content :deep(.data-table td:nth-child(2)) {
  min-width: 120px;
  word-break: break-word;
}

.report-content :deep(.data-table td:nth-child(3)) {
  @include font-weight-regular;
  min-width: 120px;
  white-space: nowrap;
}

.report-content :deep(.data-table td:nth-child(4)) {
  min-width: 120px;
  word-break: break-word;
}

.report-content :deep(.data-table.bordered) {
  border: 1px solid #000;
}

.report-content :deep(.data-table.bordered th),
.report-content :deep(.data-table.bordered td) {
  border: 1px solid #000;
  padding: $spacing-2;
  text-align: left;
}

.report-content :deep(.data-table.bordered thead tr) {
  background-color: var(--vt-c-white-soft);
}

.report-content :deep(.data-table.bordered th) {
  @include font-weight-bold;
}

.report-content :deep(.highlight) {
  color: #d2691e;
}

.report-content :deep(.email-link) {
  color: #0066cc;
  text-decoration: underline;
}

.report-content :deep(.report-footer) {
  @include typography-caption;
  margin-top: 40px;
  text-align: center;
  color: #666;
}

.report-content :deep(.report-footer p) {
  margin: $spacing-1 0;
}

.report-content :deep(.address-item) {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.report-content :deep(.address-item:last-child) {
  border-bottom: none;
}

.report-content :deep(.address-item h3) {
  @include typography-small;
  @include font-weight-bold;
  margin: 0 0 10px 0;
  color: #333;
  padding-bottom: 3px;
  border-bottom: 1px solid #cccccc;
}

/* PDF page break controls */
.report-content :deep(.report-section) {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
  page-break-before: auto;
  page-break-after: auto;
}

.report-content :deep(.address-item) {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
  page-break-before: avoid;
  page-break-after: avoid;
  margin-bottom: 15px;
}

.report-content :deep(.data-table) {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
  page-break-before: avoid;
  page-break-after: avoid;
}

/* Force address section to stay together */
.report-content :deep(.report-section h2) {
  page-break-after: avoid !important;
  break-after: avoid !important;
}

/* Ensure tables don't break */
.report-content :deep(table) {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

/* Mobile responsive design */
@media (max-width: 768px) {
  .personal-data-screen {
    padding: $spacing-4;
  }
  
  .personal-data-header h3 {
    @include typography-h4;
  }
  
  .personal-data-description {
    @include typography-small;
  }
  
  .form-section {
    padding: $spacing-6 $spacing-5;
  }
  
  .submit-button {
    width: 100%;
  }
  
  .export-options {
    padding: $spacing-4;
    margin-top: 16px;
  }
  
  .export-buttons {
    flex-direction: column;
  }
  
  .export-button {
    width: 100%;
  }
  
  .report-header {
    flex-direction: column;
    gap: $gap-md;
    align-items: stretch;
  }
  
  .report-actions {
    justify-content: stretch;
  }
  
  .report-button {
    flex: 1;
  }
  
  .report-content {
    padding: $spacing-4;
    max-height: 500px;
  }
  
  .report-content :deep(.data-table td:first-child),
  .report-content :deep(.data-table td:nth-child(3)) {
    @include typography-caption;
    width: 35%;
  }
  
  .report-content :deep(.data-table td:nth-child(2)),
  .report-content :deep(.data-table td:nth-child(4)) {
    @include typography-caption;
    width: 15%;
  }
}
</style>
